import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { useToast } from '../../../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../../components/admin/BackButton';
import CompanySelector from '../../../../components/admin/procurement/CompanySelector';
import BankAccountSelector from '../../../../components/admin/procurement/BankAccountSelector';
import ProductAutocomplete from '../../../../components/shared/ProductAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreatePurchaseOrder = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  // Step 1: Company & Bank
  const [companyId, setCompanyId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');

  // Step 2: Supplier & Items
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([
    {
      product_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      unit: 'unit',
      tax_rate: 0,
      discount: 0
    }
  ]);
  const [poDate, setPoDate] = useState(new Date().toISOString().split('T')[0]);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [shippingCost, setShippingCost] = useState(0);
  const [notes, setNotes] = useState('');

  React.useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch suppliers');
      const data = await response.json();
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const addItem = () => {
    setItems([...items, {
      product_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      unit: 'unit',
      tax_rate: 0,
      discount: 0
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Recalculate item totals
    const item = newItems[index];
    const subtotal = item.quantity * item.unit_price;
    const discountAmount = subtotal * (item.discount / 100);
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = taxableAmount * (item.tax_rate / 100);
    const total = taxableAmount + taxAmount;

    newItems[index].subtotal = subtotal;
    newItems[index].tax_amount = taxAmount;
    newItems[index].total = total;

    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const taxTotal = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const discountTotal = items.reduce((sum, item) => sum + ((item.subtotal || 0) * (item.discount || 0) / 100), 0);
    const total = subtotal + taxTotal - discountTotal + Number(shippingCost);

    return { subtotal, taxTotal, discountTotal, total };
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        company_id: companyId,
        bank_account_id: bankAccountId,
        supplier_id: supplierId,
        po_date: new Date(poDate).toISOString(),
        expected_delivery_date: expectedDeliveryDate ? new Date(expectedDeliveryDate).toISOString() : null,
        items: items.map(item => ({
          product_name: item.product_name,
          description: item.description || '',
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
          unit: item.unit,
          tax_rate: Number(item.tax_rate),
          discount: Number(item.discount),
          subtotal: item.subtotal || 0,
          tax_amount: item.tax_amount || 0,
          total: item.total || 0
        })),
        currency,
        shipping_cost: Number(shippingCost),
        notes
      };

      const response = await fetch(`${API_URL}/api/procurement/purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create PO');
      }

      const po = await response.json();

      toast({
        title: 'Success',
        description: `Purchase Order ${po.po_number} created successfully`
      });

      navigate(`/admin/procurement/purchase-orders/${po.id}`);

    } catch (error) {
      console.error('Error creating PO:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = companyId && bankAccountId;
  const canProceedStep2 = supplierId && items.every(item => item.product_name && item.quantity > 0 && item.unit_price > 0);

  const totals = calculateTotals();

  return (
    <div>
      <BackButton />

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#2B2D42]">Create Purchase Order</h2>
        <p className="text-sm text-[#6C757D]">Step {currentStep} of 3</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                currentStep >= step ? 'bg-[#00B4D8] text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep > step ? <Check className="h-5 w-5" /> : step}
              </div>
              {step < 3 && (
                <div className={`flex-1 h-1 mx-2 ${
                  currentStep > step ? 'bg-[#00B4D8]' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-xs text-[#6C757D]">Company & Bank</span>
          <span className="text-xs text-[#6C757D]">Supplier & Items</span>
          <span className="text-xs text-[#6C757D]">Review & Submit</span>
        </div>
      </div>

      {/* Step 1: Company & Bank */}
      {currentStep === 1 && (
        <Card>
          <CardContent className="pt-6 space-y-6">
            <CompanySelector
              value={companyId}
              onChange={(id) => setCompanyId(id)}
              required
            />
            <BankAccountSelector
              companyId={companyId}
              value={bankAccountId}
              onChange={(id) => setBankAccountId(id)}
              required
            />
          </CardContent>
        </Card>
      )}

      {/* Step 2: Supplier & Items */}
      {currentStep === 2 && (
        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Supplier */}
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Supplier <span className="text-red-500">*</span>
              </label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              >
                <option value="">Select supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.company_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Dates & Currency */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">PO Date</label>
                <input
                  type="date"
                  value={poDate}
                  onChange={(e) => setPoDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">Expected Delivery</label>
                <input
                  type="date"
                  value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="AED">AED</option>
                </select>
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="text-sm font-medium text-[#2B2D42]">Items</label>
                <Button onClick={addItem} size="sm" variant="outline">
                  Add Item
                </Button>
              </div>

              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs text-[#6C757D] mb-1">Product Name *</label>
                        <ProductAutocomplete
                          value={item.product_name}
                          onChange={(v) => updateItem(index, 'product_name', v)}
                          onSelect={(p) => {
                            updateItem(index, 'product_name', p.name || '');
                            if (p.id) updateItem(index, 'product_id', p.id);
                            if (p.sku) updateItem(index, 'sku', p.sku);
                            if (p.price != null) updateItem(index, 'unit_price', Number(p.price));
                            if (p.tax_rate != null) updateItem(index, 'tax_rate', Number(p.tax_rate));
                            if (p.description) updateItem(index, 'description', p.description);
                          }}
                          testId={`po-item-${index}-product`}
                          placeholder="Pesquise ou crie novo produto…"
                          inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#6C757D] mb-1">Quantity *</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                          min="1"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#6C757D] mb-1">Unit Price *</label>
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', Number(e.target.value))}
                          min="0"
                          step="0.01"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#6C757D] mb-1">Tax Rate (%)</label>
                        <input
                          type="number"
                          value={item.tax_rate}
                          onChange={(e) => updateItem(index, 'tax_rate', Number(e.target.value))}
                          min="0"
                          max="100"
                          step="0.1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#6C757D] mb-1">Discount (%)</label>
                        <input
                          type="number"
                          value={item.discount}
                          onChange={(e) => updateItem(index, 'discount', Number(e.target.value))}
                          min="0"
                          max="100"
                          step="0.1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                        />
                      </div>
                      <div className="flex items-end">
                        {items.length > 1 && (
                          <Button
                            onClick={() => removeItem(index)}
                            variant="outline"
                            size="sm"
                            className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                    {item.total > 0 && (
                      <div className="mt-2 text-sm text-[#6C757D]">
                        Total: {currency} {item.total.toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping & Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">Shipping Cost</label>
                <input
                  type="number"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(Number(e.target.value))}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review */}
      {currentStep === 3 && (
        <Card>
          <CardContent className="pt-6 space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Review Purchase Order</h3>
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                <div className="text-left">
                  <p className="text-sm text-[#6C757D]">Subtotal:</p>
                  <p className="text-lg font-semibold">{currency} {totals.subtotal.toFixed(2)}</p>
                </div>
                <div className="text-left">
                  <p className="text-sm text-[#6C757D]">Tax:</p>
                  <p className="text-lg font-semibold">{currency} {totals.taxTotal.toFixed(2)}</p>
                </div>
                <div className="text-left">
                  <p className="text-sm text-[#6C757D]">Shipping:</p>
                  <p className="text-lg font-semibold">{currency} {shippingCost.toFixed(2)}</p>
                </div>
                <div className="text-left">
                  <p className="text-sm text-[#6C757D]">Total:</p>
                  <p className="text-2xl font-bold text-[#00B4D8]">{currency} {totals.total.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6">
        <Button
          onClick={() => setCurrentStep(currentStep - 1)}
          disabled={currentStep === 1}
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        
        {currentStep < 3 ? (
          <Button
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={(currentStep === 1 && !canProceedStep1) || (currentStep === 2 && !canProceedStep2)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? 'Creating...' : 'Create Purchase Order'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreatePurchaseOrder;
